import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ChevronRight, MessageCircle } from 'lucide-react';

/**
 * GermanyResourceShell
 * --------------------
 * Shared layout chrome for every Germany topical spoke page (Phase 2 of the
 * Hub-and-Spoke architecture rollout — Feb 2026).
 *
 * Responsibilities:
 *   • Visible top breadcrumbs (Home → Study Abroad → Germany → {spoke})
 *   • Injects BreadcrumbList + Article + FAQPage JSON-LD schema
 *   • Back-to-Germany-Hub CTA at the bottom of every page
 *   • "Related Resources" mini-grid linking to 3+ sibling spokes
 *   • Inline counselling CTA card (lead capture)
 *
 * Usage:
 *   <GermanyResourceShell
 *     slug="aps-certificate"
 *     breadcrumbLabel="APS Certificate"
 *     articleHeadline="APS Certificate Germany — Complete Guide"
 *     articleDescription="..."
 *     datePublished="2026-02-16"
 *     dateModified="2026-02-16"
 *     faqs={[{ q, a }, ...]}
 *     related={['blocked-account', 'student-visa', 'cost-of-study']}
 *   >
 *     <YourPageContent />
 *   </GermanyResourceShell>
 *
 * The shell is deliberately a render-prop / children wrapper so each spoke
 * can keep total control over its hero and main body content.
 */

const ALL_GERMANY_SPOKES = {
  'student-visa':         { label: 'Germany Student Visa',     short: 'Visa requirements, fees, documents & timeline.' },
  'aps-certificate':      { label: 'APS Certificate',          short: 'Mandatory academic verification for Indians.' },
  'blocked-account':      { label: 'Blocked Account',          short: '€11,904 funding proof — providers compared.' },
  'cost-of-study':        { label: 'Cost of Study',            short: 'Tuition, living, insurance — full 2026 budget.' },
  'scholarships':         { label: 'Scholarships',             short: 'DAAD, Erasmus+, Deutschlandstipendium & more.' },
  'public-universities':  { label: 'Public Universities',      short: 'Tuition-free TU9 + state-recognised list.' },
  'ausbildung':           { label: 'Ausbildung',               short: 'Paid 3-year vocational training pathway.' },
  'pr-after-study':       { label: 'PR After Study',           short: '21-month Blue Card → PR roadmap.' },
  'intakes':              { label: 'Intakes',                  short: 'Winter (Oct) & Summer (Apr) timelines.' },
  'student-accommodation':{ label: 'Student Accommodation',    short: 'Studentenwerk, WGs, private flats.' },
  'jobs-after-masters':   { label: 'Jobs After Masters',       short: '18-month job-seeker visa + employer list.' },
};

const SHELL_ID = 'germany-resource-shell-schema';

const GermanyResourceShell = ({
  slug,
  breadcrumbLabel,
  articleHeadline,
  articleDescription,
  datePublished = '2026-02-16',
  dateModified  = '2026-02-16',
  faqs = [],
  related = [],
  children,
}) => {
  // Inject Breadcrumb + Article + FAQPage JSON-LD in one combined script.
  useEffect(() => {
    document.getElementById(SHELL_ID)?.remove();
    const url = `https://zafco.in/study-abroad/germany/${slug}/`;

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'Germany',      item: 'https://zafco.in/study-abroad/germany/' },
        { '@type': 'ListItem', position: 4, name: breadcrumbLabel },
      ],
    };

    const article = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: articleHeadline,
      description: articleDescription,
      image: 'https://zafco.in/zafco-logo.png',
      datePublished,
      dateModified,
      author:    { '@type': 'Organization', '@id': 'https://zafco.in/#organization', name: 'Zafco Study Abroad', url: 'https://zafco.in/study-abroad/' },
      publisher: { '@type': 'Organization', '@id': 'https://zafco.in/#organization', name: 'Zafco Human Resource Management', logo: { '@type': 'ImageObject', url: 'https://zafco.in/zafco-logo.png', width: 600, height: 60 } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      isPartOf: { '@id': 'https://zafco.in/#website' },
      inLanguage: 'en-IN',
    };

    const tags = [
      Object.assign(document.createElement('script'), { type: 'application/ld+json', text: JSON.stringify(breadcrumb) }),
      Object.assign(document.createElement('script'), { type: 'application/ld+json', text: JSON.stringify(article)    }),
    ];

    if (faqs.length) {
      const faqLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      };
      tags.push(Object.assign(document.createElement('script'), { type: 'application/ld+json', text: JSON.stringify(faqLd) }));
    }

    // Group under a parent <div> with an id so we can clean up atomically.
    const holder = document.createElement('div');
    holder.id = SHELL_ID;
    holder.style.display = 'none';
    tags.forEach((t) => holder.appendChild(t));
    document.head.appendChild(holder);

    return () => document.getElementById(SHELL_ID)?.remove();
  }, [slug, breadcrumbLabel, articleHeadline, articleDescription, datePublished, dateModified, faqs]);

  return (
    <div className="bg-white">
      {/* Visible top breadcrumb (crawlable + accessible) */}
      <nav
        aria-label="Breadcrumb"
        className="bg-slate-50 border-b border-slate-200"
        data-testid={`breadcrumb-germany-${slug}`}
      >
        <ol className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-1.5 text-xs text-slate-600 overflow-x-auto whitespace-nowrap">
          <li><Link to="/"                       className="hover:text-[#003087] hover:underline">Home</Link></li>
          <li className="text-slate-400">/</li>
          <li><Link to="/study-abroad/"          className="hover:text-[#003087] hover:underline">Study Abroad</Link></li>
          <li className="text-slate-400">/</li>
          <li><Link to="/study-abroad/germany/"  className="hover:text-[#003087] hover:underline">Germany</Link></li>
          <li className="text-slate-400">/</li>
          <li className="text-slate-900 font-semibold truncate">{breadcrumbLabel}</li>
        </ol>
      </nav>

      {children}

      {/* Inline Counselling CTA — high-intent lead capture */}
      <section className="bg-[#003087]" data-testid={`spoke-cta-germany-${slug}`}>
        <div className="max-w-5xl mx-auto px-6 py-12 flex flex-col lg:flex-row items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <MessageCircle className="w-7 h-7 text-[#C5A028]" />
          </div>
          <div className="flex-1 text-center lg:text-left">
            <h3 className="text-xl font-bold font-syne text-white mb-1.5">
              Need help with {breadcrumbLabel}?
            </h3>
            <p className="text-sm text-slate-200 max-w-2xl">
              Book a free 1:1 session with a ZAFCO Germany counsellor. Document checklist, timeline planning, and direct support — no charges, no commitment.
            </p>
          </div>
          <Link
            to={`/study-abroad/book-counselling/?source=germany-${slug}`}
            className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3 rounded-full transition-all whitespace-nowrap"
            data-testid={`spoke-cta-btn-germany-${slug}`}
          >
            Book Free Counselling <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Related Resources — 3+ sibling spokes for Spoke→Spoke linking */}
      {related.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200" data-testid={`related-resources-germany-${slug}`}>
          <div className="max-w-7xl mx-auto px-6 py-14">
            <div className="flex items-center justify-between mb-7">
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-1.5">
                  Continue Your Germany Research
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087]">
                  Related Germany Resources
                </h2>
              </div>
              <Link
                to="/study-abroad/germany/"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#003087] hover:text-[#001F5C]"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Germany Hub
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((sib) => {
                const meta = ALL_GERMANY_SPOKES[sib];
                if (!meta) return null;
                return (
                  <Link
                    key={sib}
                    to={`/study-abroad/germany/${sib}/`}
                    className="group bg-white border border-slate-200 hover:border-[#003087] rounded-xl p-5 transition-all hover:shadow-md"
                    data-testid={`related-card-${sib}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-[#C5A028] uppercase tracking-wider">Germany</span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#003087] group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <h3 className="text-base font-bold font-syne text-slate-900 group-hover:text-[#003087] mb-1.5">
                      {meta.label}
                    </h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{meta.short}</p>
                  </Link>
                );
              })}
            </div>
            <div className="sm:hidden mt-6 text-center">
              <Link
                to="/study-abroad/germany/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#003087]"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Germany Hub
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default GermanyResourceShell;
